import { ConfigStateService, CoreModule, PermissionService } from '@abp/ng.core';
import { ThemeSharedModule } from '@abp/ng.theme.shared';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { APP_VERSION } from './app-version';
import { CHANGELOG } from './changelog';

const LABELS = {
  en: { version: 'Version', history: 'Update history', close: 'Close' },
  ar: { version: 'الإصدار', history: 'سجل التحديثات', close: 'إغلاق' },
};

/**
 * The build's version under the sidebar logo (#90). Everyone sees it; admins can click it to read
 * the update history from changelog.ts.
 */
@Component({
  selector: 'app-version',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CoreModule, ThemeSharedModule],
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (isAdmin()) {
      <button type="button" class="app-version app-version-link" [title]="labels().history" (click)="open.set(true)">
        {{ labels().version }} {{ version }}
      </button>
    } @else {
      <span class="app-version">{{ labels().version }} {{ version }}</span>
    }

    <abp-modal [(visible)]="open" [options]="{ size: 'lg', scrollable: true }">
      <ng-template #abpHeader>
        <h3>{{ labels().history }}</h3>
      </ng-template>
      <ng-template #abpBody>
        <p class="text-muted mb-3">{{ labels().version }} {{ version }}</p>
        @for (entry of changelog; track entry.date) {
          <section class="mb-3">
            <h6 class="mb-1" dir="ltr" [style.text-align]="'start'">{{ entry.date }}</h6>
            <ul class="mb-0 ps-3">
              @for (line of entry[lang()]; track $index) {
                <li>{{ line }}</li>
              }
            </ul>
          </section>
        }
      </ng-template>
      <ng-template #abpFooter>
        <button type="button" class="btn btn-secondary" abpClose>{{ labels().close }}</button>
      </ng-template>
    </abp-modal>
  `,
  styles: `
    /* The logo container is fixed at the sidebar's top and the menu starts 72px down, right below
       it. Anything that makes the container taller covers the first menu item (#91), so the
       version sits in the gap under the logo instead of in the flow. */
    .app-version {
      position: absolute;
      bottom: 4px;
      inset-inline-start: 20px;
      padding: 0;
      line-height: 1;
      font-size: 0.75rem;
      opacity: 0.6;
      white-space: nowrap;
      text-align: start;
    }
    .app-version-link {
      background: none;
      border: 0;
      color: inherit;
      cursor: pointer;
    }
    .app-version-link:hover {
      opacity: 1;
      text-decoration: underline;
    }
    /* The collapsed sidebar is 72px wide, too narrow for the text; it shows again on hover. */
    @media (min-width: 768px) and (max-width: 1199px) {
      .lpx-sidebar-container:not(:hover) .app-version {
        display: none;
      }
    }
    @media (min-width: 1200px) {
      #lpx-wrapper.hover-trigger .lpx-sidebar-container:not(:hover) .app-version {
        display: none;
      }
    }
  `,
})
export class AppVersionComponent {
  private readonly config = inject(ConfigStateService);
  private readonly permissions = inject(PermissionService);

  readonly version = APP_VERSION;
  readonly changelog = CHANGELOG;
  readonly open = signal(false);

  private readonly culture = toSignal(this.config.getDeep$('localization.currentCulture.name'));
  readonly lang = computed<'en' | 'ar'>(() => (String(this.culture() ?? '').startsWith('ar') ? 'ar' : 'en'));
  readonly labels = computed(() => LABELS[this.lang()]);

  private readonly grantedPolicies = toSignal(this.config.getDeep$('auth.grantedPolicies'));
  readonly isAdmin = computed(() => {
    this.grantedPolicies();
    return this.permissions.getGrantedPolicy('ReturnLy.Dashboard.Host.AdminDashboard');
  });
}
